import {
	CheckboxControl,
	Icon,
	Loading,
	Separator,
	TextLink,
	useDebounce,
	useFormatDateTime,
	useI18nForm,
} from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { useField, useFormikContext } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { eventService } from "../../api/eventService";
import { SimilarEventRequest } from "../../types/event";
import { CreateEventInitialValues, SimilarEvents } from "./CreateEventProvider";

const StyledList = styled.ul`
	margin: ${({ theme }) => theme.spacing(4)} 0;
	list-style: none;
`;

const Heading = styled.span`
	display: block;
	padding-top: ${({ theme }) => theme.spacing(4)};
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
`;

const StyledTextLink = styled(TextLink)`
	display: inline-flex;
`;

const name = "hasSimilarEvents";

interface Props {
	basePath: string;
}

export function SimilarEventsField({ basePath }: Props) {
	const { t } = useTranslation();
	const { fallbackLanguage } = useI18nForm();
	const [ignored, setIgnored] = useState<boolean>(false);
	const [{ value, onChange }, meta] = useField<SimilarEvents>(name);
	const { values } = useFormikContext<CreateEventInitialValues>();
	const formatDateTime = useFormatDateTime();
	const request: SimilarEventRequest | null = useMemo(
		() =>
			values.translations.find((translation) => translation.locale === fallbackLanguage)?.name &&
			values.start &&
			values.end
				? {
						name: values.translations.find((translation) => translation.locale === fallbackLanguage)?.name as string,
						location: values.location?.name || null,
						start: values.start,
						end: values.end,
						eventId: "",
					}
				: null,
		[fallbackLanguage, values.location, values.end, values.start, values.translations],
	);
	const debouncedRequest = useDebounce(request, 300);

	useEffect(() => {
		if (debouncedRequest !== null) {
			onChange({ target: { name, value: SimilarEvents.PENDING } });
		}
	}, [debouncedRequest, onChange]);

	const { data: similarEvents = [], isLoading } = useQuery({
		queryKey: eventService.similarEventsKey.concat(JSON.stringify(debouncedRequest)),
		queryFn: () =>
			eventService.getSimilarEvents(debouncedRequest as SimilarEventRequest).then((res) => {
				onChange({ target: { name, value: res.data.data.length > 0 ? SimilarEvents.TRUE : SimilarEvents.NONE } });
				return res.data.data;
			}),
		enabled: !!debouncedRequest,
	});

	return (
		<>
			{similarEvents.length > 0 && (
				<>
					<Separator />
					<Heading>
						{t("This event is similar to the following event. Please make sure to avoid creating any duplicates.", {
							count: similarEvents.length,
						})}
					</Heading>
					<StyledList>
						{similarEvents.map((event) => (
							<li key={event.id}>
								<StyledTextLink prefix={<Icon $icon="external_link" />} to={`${basePath}/${event.id}`} target="_blank">
									{event.name} {event.location && `- ${event.location.name}`} ({formatDateTime(event.start)} -{" "}
									{formatDateTime(event.end)})
								</StyledTextLink>
							</li>
						))}
					</StyledList>
				</>
			)}
			{isLoading && <Loading />}
			{value === SimilarEvents.TRUE || value === SimilarEvents.IGNORED ? (
				<CheckboxControl
					label={t("None of the suggested events match.")}
					name={name}
					error={meta.touched ? meta.error : undefined}
					value={ignored}
					onChange={(e) => {
						setIgnored(e.target.value);
						onChange({ target: { name, value: e.target.value ? SimilarEvents.IGNORED : SimilarEvents.TRUE } });
					}}
				/>
			) : null}
			{value === SimilarEvents.NONE && <div data-testid="no-similar-events" />}
		</>
	);
}
